import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { fetchMinuta, registrarCobro } from '@/services/cobranzas.js';
import MinutaHeader from './components/MinutaHeader.jsx';
import { hasPermission } from '@/utils/permissions.js';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-AR');
};

const MEDIOS_PAGO = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'deposito', label: 'Depósito' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'otro', label: 'Otro' },
];

export default function MinutaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, sucursal } = useAuth();
  const [loading, setLoading] = useState(false);
  const [minutaData, setMinutaData] = useState(null);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    cliente: '',
    fechaDesde: '',
    fechaHasta: '',
  });
  const [selectedDocumento, setSelectedDocumento] = useState(null);
  const [cobroForm, setCobroForm] = useState({
    medio_pago: 'efectivo',
    importe: '',
    referencia: '',
    fecha: new Date().toISOString().split('T')[0],
  });
  const [cobroError, setCobroError] = useState('');
  const [cobroLoading, setCobroLoading] = useState(false);
  const canRegistrar = hasPermission(user, 'COBRANZA_REGISTRAR');

  const minuta = minutaData?.minuta;
  const documentos = minutaData?.documentos || [];
  const cobros = minutaData?.cobros || [];
  const resumenMedios = minutaData?.resumenMedios || [];

  const isReadOnly = minuta?.estado === 'CERRADA';

  const loadMinuta = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchMinuta({
        id,
        cliente: filters.cliente,
        fechaDesde: filters.fechaDesde,
        fechaHasta: filters.fechaHasta,
      });
      setMinutaData(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'No se pudo obtener la minuta.');
    } finally {
      setLoading(false);
    }
  }, [filters.cliente, filters.fechaDesde, filters.fechaHasta, id]);

  useEffect(() => {
    loadMinuta();
  }, [loadMinuta]);

  const handleSelectDocumento = (documento) => {
    setSelectedDocumento(documento);
    setCobroForm((prev) => ({
      ...prev,
      importe: documento?.saldo ? String(documento.saldo) : prev.importe,
    }));
  };

  const handleRegistrarCobro = async (event) => {
    event.preventDefault();
    setCobroError('');

    if (!minuta?.id) {
      setCobroError('No hay minuta activa.');
      return;
    }
    if (!selectedDocumento) {
      setCobroError('Seleccioná un documento para cobrar.');
      return;
    }
    if (!cobroForm.importe || Number(cobroForm.importe) <= 0) {
      setCobroError('Indicá un importe válido.');
      return;
    }

    setCobroLoading(true);
    try {
      await registrarCobro({
        minuta_id: minuta.id,
        sucursal_id: sucursal?.id || '',
        documento_tipo: selectedDocumento.tipo,
        documento_id: selectedDocumento.id,
        medio_pago: cobroForm.medio_pago,
        importe: Number(cobroForm.importe),
        referencia: cobroForm.referencia,
        fecha: cobroForm.fecha,
        usuario_id: user?.id || null,
      });
      setCobroForm((prev) => ({
        ...prev,
        importe: '',
        referencia: '',
      }));
      setSelectedDocumento(null);
      await loadMinuta();
    } catch (err) {
      setCobroError(err?.response?.data?.message || 'No se pudo registrar el cobro.');
    } finally {
      setCobroLoading(false);
    }
  };

  const resumenTotal = useMemo(() => {
    return resumenMedios.reduce((acc, medio) => acc + Number(medio.total || 0), 0);
  }, [resumenMedios]);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-6 py-6">
      <PageHeader
        title={minuta ? `Minuta N.º ${minuta.numero || minuta.id} · ${minuta.sucursal_nombre || 'Sucursal'}` : 'Minuta de Cobranza'}
        description="Registrá cobros de facturas y guías dentro de la minuta activa."
        actions={
          canRegistrar ? (
            <button
              type="button"
              onClick={() => navigate(`/cobranzas/minuta/${minuta?.id}/cerrar`)}
              className="rounded-xl border border-[rgba(var(--border),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.2)]"
              disabled={!minuta?.id}
            >
              Ir al cierre
            </button>
          ) : null
        }
      />
      <MinutaHeader
        minuta={minuta}
        showCerrar={!isReadOnly && canRegistrar}
        onCerrar={() => navigate(`/cobranzas/minuta/${minuta?.id}/cerrar`)}
      />
      {!canRegistrar ? (
        <div className="mt-4 rounded-2xl border border-[rgba(var(--danger,255_99_99),_0.4)] bg-[rgba(var(--danger,255_99_99),_0.15)] p-4 text-sm text-[rgb(var(--danger,255_99_99))]">
          No tenés permisos para registrar cobranzas o cerrar minutas.
        </div>
      ) : null}
      {error ? <p className="mt-4 text-sm text-red-500">{error}</p> : null}
      <div className="mt-8 grid gap-6 lg:grid-cols-[2fr,1fr]">
        <section className="rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.85)] p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Documentos cobrables</h2>
              <p className="mt-1 text-sm text-[rgba(var(--muted),0.85)]">
                Facturas pendientes y guías sin factura disponibles para cobrar.
              </p>
            </div>
            <button
              type="button"
              onClick={loadMinuta}
              className="rounded-xl border border-[rgba(var(--border),0.7)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.2)]"
              disabled={loading}
            >
              Actualizar
            </button>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <div>
              <label className="block text-xs font-semibold uppercase text-[rgba(var(--muted),0.8)]">Cliente</label>
              <input
                type="text"
                value={filters.cliente}
                onChange={(event) => setFilters({ ...filters, cliente: event.target.value })}
                placeholder="ID o razón social"
                className="mt-2 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-transparent px-3 py-2 text-sm text-[rgb(var(--fg))]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-[rgba(var(--muted),0.8)]">Fecha desde</label>
              <input
                type="date"
                value={filters.fechaDesde}
                onChange={(event) => setFilters({ ...filters, fechaDesde: event.target.value })}
                className="mt-2 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-transparent px-3 py-2 text-sm text-[rgb(var(--fg))]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-[rgba(var(--muted),0.8)]">Fecha hasta</label>
              <input
                type="date"
                value={filters.fechaHasta}
                onChange={(event) => setFilters({ ...filters, fechaHasta: event.target.value })}
                className="mt-2 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-transparent px-3 py-2 text-sm text-[rgb(var(--fg))]"
              />
            </div>
          </div>
          <div className="mt-6 overflow-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase text-[rgba(var(--muted),0.8)]">
                  <th className="px-3 py-2">Tipo</th>
                  <th className="px-3 py-2">Documento</th>
                  <th className="px-3 py-2">Cliente</th>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Saldo</th>
                  <th className="px-3 py-2 text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                {documentos.length ? (
                  documentos.map((doc) => (
                    <tr
                      key={`${doc.tipo}-${doc.id}`}
                      className="border-t border-[rgba(var(--border),0.6)] text-sm text-[rgb(var(--fg))]"
                    >
                      <td className="px-3 py-3 font-semibold">
                        <span className="rounded-full bg-[rgba(var(--accent),0.12)] px-3 py-1 text-xs font-semibold text-[rgb(var(--accent))]">
                          {doc.tipo}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-medium">{doc.documento_numero || `#${doc.id}`}</div>
                        <div className="text-xs text-[rgba(var(--muted),0.8)]">{doc.estado}</div>
                      </td>
                      <td className="px-3 py-3">{doc.cliente_nombre || 'Sin cliente'}</td>
                      <td className="px-3 py-3">{formatDate(doc.doc_fecha)}</td>
                      <td className="px-3 py-3 font-semibold">{formatCurrency(doc.saldo)}</td>
                      <td className="px-3 py-3 text-right">
                        {canRegistrar ? (
                          <button
                            type="button"
                            onClick={() => handleSelectDocumento(doc)}
                            disabled={isReadOnly}
                            className="rounded-lg border border-[rgba(var(--border),0.7)] px-3 py-1 text-xs font-semibold text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.2)] disabled:opacity-50"
                          >
                            Cobrar
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="px-3 py-6 text-center text-sm text-[rgba(var(--muted),0.8)]"
                      colSpan={6}
                    >
                      No hay documentos cobrables con los filtros actuales.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="flex flex-col gap-6">
          <div className="rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.85)] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Registro de cobro</h2>
            <p className="mt-1 text-sm text-[rgba(var(--muted),0.85)]">
              Asociá el cobro a la minuta activa. Se permiten pagos parciales.
            </p>
            <div className="mt-4 rounded-xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--muted),0.1)] p-4">
              <p className="text-xs font-semibold uppercase text-[rgba(var(--muted),0.8)]">Documento seleccionado</p>
              <p className="mt-1 text-sm font-semibold text-[rgb(var(--fg))]">
                {selectedDocumento
                  ? `${selectedDocumento.tipo} · ${selectedDocumento.documento_numero || `#${selectedDocumento.id}`}`
                  : 'Sin seleccionar'}
              </p>
              {selectedDocumento ? (
                <p className="mt-1 text-xs text-[rgba(var(--muted),0.8)]">
                  Saldo pendiente: {formatCurrency(selectedDocumento.saldo)}
                </p>
              ) : null}
            </div>
            {canRegistrar ? (
              <form className="mt-4 grid gap-4" onSubmit={handleRegistrarCobro}>
                <div>
                  <label className="block text-xs font-semibold uppercase text-[rgba(var(--muted),0.8)]">Medio de pago</label>
                  <select
                    value={cobroForm.medio_pago}
                    onChange={(event) => setCobroForm({ ...cobroForm, medio_pago: event.target.value })}
                    className="mt-2 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-transparent px-3 py-2 text-sm text-[rgb(var(--fg))]"
                    disabled={isReadOnly}
                  >
                    {MEDIOS_PAGO.map((medio) => (
                      <option key={medio.value} value={medio.value}>
                        {medio.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-[rgba(var(--muted),0.8)]">Importe</label>
                  <input
                    type="number"
                    step="0.01"
                    value={cobroForm.importe}
                    onChange={(event) => setCobroForm({ ...cobroForm, importe: event.target.value })}
                    className="mt-2 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-transparent px-3 py-2 text-sm text-[rgb(var(--fg))]"
                    disabled={isReadOnly}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-[rgba(var(--muted),0.8)]">Referencia</label>
                  <input
                    type="text"
                    value={cobroForm.referencia}
                    onChange={(event) => setCobroForm({ ...cobroForm, referencia: event.target.value })}
                    className="mt-2 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-transparent px-3 py-2 text-sm text-[rgb(var(--fg))]"
                    disabled={isReadOnly}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-[rgba(var(--muted),0.8)]">Fecha</label>
                  <input
                    type="date"
                    value={cobroForm.fecha}
                    onChange={(event) => setCobroForm({ ...cobroForm, fecha: event.target.value })}
                    className="mt-2 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-transparent px-3 py-2 text-sm text-[rgb(var(--fg))]"
                    disabled={isReadOnly}
                  />
                </div>
                {cobroError ? <p className="text-sm text-red-500">{cobroError}</p> : null}
                <button
                  type="submit"
                  disabled={isReadOnly || cobroLoading}
                  className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow hover:opacity-90 disabled:opacity-50"
                >
                  {cobroLoading ? 'Registrando...' : 'Registrar cobro'}
                </button>
              </form>
            ) : (
              <p className="mt-4 text-sm text-[rgba(var(--muted),0.85)]">
                No tenés permisos para registrar cobros en esta minuta.
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.85)] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Resumen de cobros</h2>
            <p className="mt-1 text-sm text-[rgba(var(--muted),0.85)]">
              Detalle acumulado por medio de pago.
            </p>
            <div className="mt-4 space-y-3">
              {resumenMedios.length ? (
                resumenMedios.map((medio) => (
                  <div key={medio.medio_pago} className="flex items-center justify-between text-sm">
                    <span className="font-medium text-[rgb(var(--fg))]">{medio.medio_pago}</span>
                    <span className="font-semibold text-[rgb(var(--fg))]">{formatCurrency(medio.total)}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-[rgba(var(--muted),0.8)]">Aún no hay cobros registrados.</p>
              )}
              <div className="border-t border-[rgba(var(--border),0.6)] pt-3 text-sm font-semibold text-[rgb(var(--fg))]">
                Total: {formatCurrency(resumenTotal)}
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="mt-8 rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.85)] p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Cobros registrados</h2>
        <div className="mt-4 overflow-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase text-[rgba(var(--muted),0.8)]">
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Cliente</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {cobros.length ? (
                cobros.map((cobro) => (
                  <tr
                    key={cobro.id}
                    className="border-t border-[rgba(var(--border),0.6)] text-sm text-[rgb(var(--fg))]"
                  >
                    <td className="px-3 py-3">{formatDate(cobro.fecha_hora)}</td>
                    <td className="px-3 py-3">{cobro.cliente_nombre || 'Sin cliente'}</td>
                    <td className="px-3 py-3 capitalize">{cobro.estado || '—'}</td>
                    <td className="px-3 py-3 text-right font-semibold">{formatCurrency(cobro.total_cobrado)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-6 text-center text-sm text-[rgba(var(--muted),0.8)]"
                  >
                    No hay cobros registrados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
